import { Injectable } from '@angular/core';
import { Details } from '@details/Interfaces/details';
import { blank, nameOnly, simple, anchor, DetailsItemType, DetailsItem } from '@details/Interfaces/details-item-types';
import { LayoutOrientation } from '@common/enums/layout-orientation';
import { SupplierDetails } from '@suppliers/models/supplier-details';
import { has, DetailsBuilder } from '@details/Interfaces/details-builder';
import { DataFormatter } from '@common/services/data-formatter.service';
import { as } from '@common/string-formatter/string-formatter';

@Injectable({
  providedIn: 'root'
})
export class SupplierDetailsMapperService {

  constructor(
    private readonly formatter: DataFormatter
  ) {}

  mapToDetails(details: SupplierDetails): Details {

    const showCompanySection =  has(details.VatReg,details.RegisteredCompanyNumber);
    const showContactSection = has(details.ContactName,
      details.Phone,
      details.DirectPhone,
      details.MobilePhone,
      details.Email,
      details.Fax);

    const showRemittanceContact = has( details.RemittanceContactName, details.RemittanceEmail, details.RemittanceFax );

    const cisApplies = details.CisApplies === 'Y';
    const approvedSupplier = details.ApprovedSupplier === 'Y';

    let remittanceMethod = as(details.RemittancePreferredMethodOfContact, 'MethodOfContact');

    let correspondenceMethod = as(details.DemandsPreferredMethodOfContact, 'MethodOfContact');
   
    const hasCisRegType = details.CisRegistrationType !== 'None';
    const cisRegTypeValue = as(details.Cis4TempPerm, 'Cis4TempPermExpansion');

    const b = new DetailsBuilder(`Reference ${details.Ref}`, details);

    b.section('Supplier', LayoutOrientation.Vertical)
      .always(nameOnly(details.Name))
      .for('StrungAddress', 'Address:');

    b.conditionalSection(showCompanySection, null, LayoutOrientation.Horizontal)
      .for('VatReg', 'Vat registration:')
      .for('RegisteredCompanyNumber', 'Registered Company No:');

    b.conditionalSection(showContactSection, 'Contact', LayoutOrientation.Horizontal)
      .for('ContactName', 'Name:')
      .telFor('Phone', 'Phone:')
      .telFor('DirectPhone', 'Direct phone:')
      .telFor('MobilePhone', 'Mobile phone:')
      .mailFor('Email', 'Email:')
      .for('Fax', 'Fax:');


    b.section('Additional Information', LayoutOrientation.Horizontal)
      .conditionalItem(showRemittanceContact, simple('Remittance contact details:', null))
      .conditionalItem(showRemittanceContact && details.RemittanceContactName, simple('Name:', details.RemittanceContactName))
      .conditionalItem(showRemittanceContact && details.RemittanceEmail, anchor('Email:', `mailto:${details.RemittanceEmail}`, details.RemittanceEmail))
      .conditionalItem(showRemittanceContact && details.RemittanceFax, simple('Fax:', details.RemittanceFax))
      .conditionalItem(remittanceMethod, simple(`Remittance is sent via ${remittanceMethod}`, null));

    b.section('', LayoutOrientation.Horizontal)
      .always(nameOnly('Demands, arrears, ad-hoc contact details:'))
      .for('DemandsContactName', 'Name:' )
      .mailFor('DemandsEmail', 'Email:')
      .for('DemandsFax', 'Fax:' )
      .conditionalItem(correspondenceMethod, simple(`Correspondence is sent via ${correspondenceMethod}`, null));

    const approvedSupplierText = approvedSupplier ? 'This is an approved supplier' : 'This is not an approved supplier';

    b.conditionalSection(cisApplies, 'CIS details', LayoutOrientation.Horizontal)
      .conditionalItem(cisApplies, nameOnly('CIS applies'))
      .conditionalItem(true, simple(approvedSupplierText, ''))
      .itemFor('SubcontractorType', simple(`This subcontractor is a ${details.SubcontractorType}`, ''))
      .itemFor('DeductionType', simple(`Deductions are at the ${details.DeductionType}`, ''))
      .always(blank())
      .for('UniqueTaxpayerReference', 'Unique taxpayer ref:')
      .for('CompanyRegistrationNumber', 'Company registration no:')
      .for('NationalInsuranceNumber', 'National insurance no:')
      .always(blank())
      .conditionalItem(hasCisRegType, simple(`CIS registration type is ${cisRegTypeValue}`, ''))
      .for('AuthorisedUserName', 'CIS authorised user:')
      .for('CisRegistrationNumber', 'Registration no:')
      .conditionalItem(has(details.RegistrationValidFromDate, details.RegistrationValidToDate), simple(`Valid from ${this.formatter.formatDate(details.RegistrationValidFromDate)} to ${this.formatter.formatDate(details.RegistrationValidToDate)}`, ''));

    return b.getDetails();


  }

}
